import ImageEditor, { BackgroundColorSelector, getEditorPreset } from '@components/BlinqImageEditor';
import ImageSelector from '@components/ImageSelector/ImageSelector';
import { Scrollable, StickyBottom } from '@components/Popover/PopoverLayouts';
import { cardEditContext } from '@context/card-edit-context';
import {
  type PicturePopoverCallbacks,
  useEditPicturePopover,
} from '@hooks/card/image/use-edit-picture-popover';
import type { PositionerOptions } from '@modules/popover-helpers';
import { Box, Button, Dialog, DialogContent, DialogFooter, DialogHeader, Inline } from '@ui-lib';
import { useContext, useEffect, useState } from 'react';

import { CardItemEditor } from '../CardItemEditor';
import { DeleteDialog } from '../DeleteDialog';
import { Panel } from '../EditCardPopover/EditCardPopover';
import { DeleteButton } from '../EditDialog/DeleteButton';
import { ResponsiveDialog } from '../ResponsiveDialog';
import { UnlinkConfirmationDialog } from '../UnlinkConfirmationDialog';
import styles from './CardHeader.module.scss';
import type { ImageType } from './types';
import { useDisableIOSPullToRefresh } from './utilities';
import type { CardImage, ValueProvider } from '.cache/__types__';

const IMAGE_EDITOR_CANVAS_HEIGHT = 190;

function maxDimensionForType(imageType: ImageType): number {
  switch (imageType) {
    case 'profile':
      return 1200;
    case 'logo':
      return 1200;
    case 'cover':
      return 1500;
    default:
      return 1500;
  }
}

/** Simple function to return a more readable string.
 * When src is falsy, we are adding, otherwise editing
 * Transforms the short type name to the human friendly name
 */
function friendlyPopoverTitle(type: ImageType, src: string | undefined | null): string {
  const result = {
    firstWord: src ? 'Edit' : 'Add',
    secondWord: 'image',
  };
  switch (type) {
    case 'cover': {
      result.secondWord = 'cover photo';
      break;
    }
    case 'logo': {
      result.secondWord = 'company logo';
      break;
    }
    case 'profile': {
      result.secondWord = 'profile picture';
      break;
    }
  }
  return `${result.firstWord} ${result.secondWord}`;
}

export interface PicturePopoverProps extends PicturePopoverCallbacks {
  canLock: boolean;
  imageReference: Partial<CardImage> | undefined | null;
  isTemplate: boolean;
  templateId: string | undefined | null;
  cardId: string | undefined | null;
  userId: string | undefined | null;
  workspaceId: string | undefined | null;
  positionerOptions?: PositionerOptions;
  show: boolean;
  targetNode: HTMLElement | undefined;
  type: ImageType;
}

export const EditPicturePopover: React.FC<React.PropsWithChildren<PicturePopoverProps>> = (props) => {
  const {
    targetNode,
    show,
    imageReference,
    type,
    positionerOptions,
    isTemplate,
    cardId,
    templateId,
    userId,
    workspaceId,
    ...callbacks
  } = props;
  const [tempValueProviderForTour, setTempValueProviderForTour] = useState<ValueProvider | null>(null);

  const { flavour } = useContext(cardEditContext);

  const {
    canLock,
    editorState,
    fieldsChanged,
    handlers,
    isLocked,
    saving,
    isFieldLinkedToProfile,
    editedImageReference,
    showDeleteWarning,
    showUnlinkWarning,
    updateEditorState,
  } = useEditPicturePopover(
    { type, imageReference, show, isTemplate, templateId, cardId, userId, workspaceId, flavour },
    callbacks,
  );

  const {
    handleDelete,
    handleCancel,
    handleImageSelect,
    handleLowQualityImageUpdate,
    handleHighQualityImageUpdate,
    handleClosePopover,
    handleSave,
    handleCancelDeleteWarning,
    handleDeletePress,
    handleCancelUnlink,
    handleConfirmUnlink,
    handleSetValueProvider,
    handleSetInstruction,
    handleSetIsOptional,
  } = handlers;

  useDisableIOSPullToRefresh();

  const src = imageReference?.original_url;
  // if the image has no editor version, then we assume it is an old card, and therefore needs some zoom correction.
  const correctionCoefficient = imageReference?.metadata?.editor_version === undefined ? 0.98 : undefined;

  useEffect(() => {
    if (targetNode && show) {
      // choose behavior: auto so that the popover doesnt reposition awkwardly
      targetNode.scrollIntoView({ behavior: 'auto', block: 'center' });
    }
  }, [targetNode, show]);

  const shouldLock = isLocked && !canLock;
  if (shouldLock) {
    return null;
  }

  const disableSave =
    !fieldsChanged ||
    (editedImageReference?.value_provider === 'template' && !editedImageReference?.edited_url) ||
    (editedImageReference?.value_provider === 'admin' && !editedImageReference?.edited_url);

  return (
    <ResponsiveDialog
      show={show}
      updateTempValueProviderForTour={setTempValueProviderForTour}
      handleClose={handleClosePopover}
      targetNode={targetNode}
      positionDeps={[src, editorState?.loaded]}
    >
      {showDeleteWarning ? (
        <DeleteDialog
          title={friendlyPopoverTitle(type, src)}
          onCancel={handleCancelDeleteWarning}
          onClose={handleCancelDeleteWarning}
          onSubmit={handleDelete}
        />
      ) : isFieldLinkedToProfile && showUnlinkWarning ? (
        <UnlinkConfirmationDialog
          title={friendlyPopoverTitle(type, src)}
          onCancel={handleCancelUnlink}
          onSave={handleConfirmUnlink}
        />
      ) : (
        <Dialog>
          <DialogHeader title={friendlyPopoverTitle(type, src)} onClose={handleClosePopover} />
          <DialogContent>
            <Scrollable>
              <CardItemEditor
                valueProvider={editedImageReference?.value_provider ?? undefined}
                tempValueProviderForTour={tempValueProviderForTour}
                setValueProvider={handleSetValueProvider}
                isTemplated={Boolean(imageReference?.template_id)}
                instruction={editedImageReference?.instruction_text ?? undefined}
                setInstruction={handleSetInstruction}
                isOptional={editedImageReference?.is_optional ?? false}
                setIsOptional={handleSetIsOptional}
                stage={editedImageReference?.stage ?? undefined}
              >
                <>
                  {!src && (
                    <div style={{ height: '132px' }}>
                      <ImageSelector
                        maxDimension={maxDimensionForType(type)}
                        minDimension={5}
                        onSelect={(original_url) =>
                          handleImageSelect({
                            ...editorState,
                            ...editedImageReference,
                            original_url,
                            metadata: { editor_version: 'web_selector_v1' },
                          })
                        }
                      />
                    </div>
                  )}

                  {src && (
                    <>
                      <div className={styles['editor-container']}>
                        <ImageEditor
                          key={type}
                          {...getEditorPreset(type)}
                          canvasHeight={IMAGE_EDITOR_CANVAS_HEIGHT}
                          focusOnMount={true}
                          imgSrc={src}
                          editorState={editorState}
                          updateEditorState={updateEditorState}
                          onHighQualityChanged={handleHighQualityImageUpdate}
                          onLowQualityChanged={handleLowQualityImageUpdate}
                          zoomCorrectionCoefficient={correctionCoefficient}
                        />
                      </div>
                      {!getEditorPreset(type).disableBgEdit && !getEditorPreset(type).imgCover && (
                        <Panel>
                          <BackgroundColorSelector
                            suggestedColors={editorState.suggestedbackgroundColors}
                            color={editorState.backgroundColor || 0}
                            setColor={(color) =>
                              updateEditorState({
                                type: 'SET_BG_COLOR',
                                color,
                                showMessage: true,
                              })
                            }
                            isCustomSelectOpen={!!editorState.showCustomBackgroundColorSelector}
                            setIsCustomSelectOpen={(value) => {
                              updateEditorState({
                                type: 'SHOW_CUSTOM_BG_SELECTOR',
                                value,
                              });
                            }}
                          />
                        </Panel>
                      )}
                    </>
                  )}
                </>
              </CardItemEditor>
            </Scrollable>
          </DialogContent>
          <StickyBottom>
            <DialogFooter>
              <Box display="flex" justifyContent="spaceBetween">
                <DeleteButton onClick={handleDeletePress} />
                <Inline gap="xsmall">
                  <Button variant="outline" type="button" onClick={handleCancel}>
                    Cancel
                  </Button>
                  <Button
                    variant="solid"
                    onClick={handleSave}
                    disabled={disableSave}
                    type="button"
                    loading={saving}
                  >
                    Save
                  </Button>
                </Inline>
              </Box>
            </DialogFooter>
          </StickyBottom>
        </Dialog>
      )}
    </ResponsiveDialog>
  );
};
